import { useEffect, useState } from "react";
import { Linkedin } from "lucide-react";
import { getTeam } from "@/api";
import { useSeo, ORG_SCHEMA } from "@/seo";
import { Reveal, SectionHead, Eyebrow } from "@/components/Reveal";
import { CalendlyCta } from "@/components/SiteChrome";

const OFFICE_IMG = "https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const VALUES = [
  { title: "Intent", body: "We take on work we can stand behind, and we say so when we cannot." },
  { title: "Trust", body: "Our ethos. Files we would defend as our own, and advice we would take ourselves." },
  { title: "Lasting impact", body: "Structures built to hold for decades, not quarters." },
];

export default function About() {
  const [team, setTeam] = useState([]);
  useSeo({
    title: "About TruVerse Middle East, India UAE Advisory from Chennai and Dubai",
    description: "TruVerse serves multinationals, Indian promoters, NRIs and UHNI families across 126 countries, from Chennai HQ and Dubai.",
    schema: ORG_SCHEMA,
  });
  useEffect(() => {
    getTeam().then(setTeam).catch(() => {});
  }, []);

  const founder = team.find((t) => t.doc_id === "team_vikram");
  const rest = team.filter((t) => t.doc_id !== "team_vikram");

  return (
    <div data-testid="about-page">
      <section className="bg-ink py-24 lg:py-32">
        <div className="container-site max-w-4xl">
          <Reveal>
            <h1 className="mt-2 text-4xl font-bold leading-tight tracking-tight md:text-6xl">
              One firm. Two homes.
              <span className="block">126 countries of context.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-muted md:text-lg">
              TruVerse is an India-headquartered advisory firm serving multinationals, Indian promoters, NRIs and UHNI
              families across 126 countries. TruVerse Middle East brings that depth to Dubai, close to the clients who
              live, invest and build between the Gulf and India.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24" data-testid="about-story-section">
        <div className="container-site grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <img src={OFFICE_IMG} alt="TruVerse office environment" className="h-auto w-full border border-border object-cover" loading="lazy" data-testid="about-office-image" />
          </Reveal>
          <Reveal delay={0.1}>
            <SectionHead
              eyebrow="The story"
              title="Chennai roots. Dubai presence."
              intro="From our Chennai headquarters we have spent years inside the tax, regulatory and wealth questions of cross-border families and companies. Dubai is where many of those questions now live. Our Middle East practice answers them with the same team, the same standards and a shorter flight."
            />
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="border border-border bg-card p-6" data-testid="about-office-chennai">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Chennai (Headquarters)</p>
                <p className="mt-2 text-sm text-muted-foreground">Willingdon Crescent, 4th Floor, #6/2, Dr. S.S. Badrinath Road, Nungambakkam, Chennai 600 006</p>
              </div>
              <div className="border border-border bg-card p-6" data-testid="about-office-dubai">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Dubai</p>
                <p className="mt-2 text-sm text-muted-foreground">[Dubai office address, add in admin]</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-secondary/50 py-24" data-testid="about-values-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="Values" title="What we optimise for." />
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="border-t-2 border-accent pt-8" data-testid={`about-value-${v.title.toLowerCase()}`}>
                  <h3 className="text-2xl text-foreground">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="about-team-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="Leadership" title="The people behind the advice." />
          </Reveal>
          {founder && (
            <Reveal delay={0.05}>
              <div className="mt-14 grid gap-10 border border-border bg-card p-8 lg:grid-cols-4 lg:p-12" data-testid="about-founder-card">
                <img
                  src="/founder.webp"
                  alt="Vikram Jain, Founder of TruVerse"
                  className="h-auto w-full object-cover"
                  loading="lazy"
                  data-testid="about-founder-portrait"
                />
                <div className="lg:col-span-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">{founder.role}</p>
                  <h3 className="mt-2 text-3xl text-foreground">{founder.name}</h3>
                  <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">{founder.bio}</p>
                  {founder.linkedin && (
                    <a href={founder.linkedin} target="_blank" rel="noopener noreferrer" data-testid="about-founder-linkedin" className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-accent hover:text-foreground">
                      <Linkedin size={14} /> LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          )}
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            {rest.map((m, i) => (
              <Reveal key={m.doc_id} delay={i * 0.08}>
                <div className="flex h-full gap-6 border border-border bg-card p-8" data-testid={`about-team-card-${m.doc_id}`}>
                  {m.image && (
                    <img src={m.image} alt={m.name} className="h-20 w-20 shrink-0 rounded-sm object-cover" loading="lazy" data-testid={`about-team-photo-${m.doc_id}`} />
                  )}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">{m.role}</p>
                    <h3 className="mt-2 text-xl text-foreground">{m.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.bio}</p>
                    {m.linkedin && (
                      <a href={m.linkedin} target="_blank" rel="noopener noreferrer" data-testid={`about-team-linkedin-${m.doc_id}`} className="mt-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-accent hover:text-foreground">
                        <Linkedin size={13} /> LinkedIn
                      </a>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-20" data-testid="about-final-cta">
        <div className="container-site flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <h2 className="max-w-xl text-3xl tracking-tight md:text-4xl">Make the next move inevitable.</h2>
          <CalendlyCta testId="about-book-btn" />
        </div>
      </section>
    </div>
  );
}
